import { useContext, useEffect, useState } from "react"

// ** Third Part Components
import { PlusCircle } from "lucide-react"
import { useMutation } from "@tanstack/react-query"

// ** Ui Components
import { Label } from "@components/common/label"
import { Avatar, AvatarImage } from "@components/common/avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"

// ** Types
import { DictType } from "@pma/types"

// ** Store
import { useAuth } from "@stores/auth-store"

// ** Service
import { editTaskService } from "@pma/services/task"

// ** Utils
import { cn } from "@pma/utils/helpers"
import { queryClient } from "@pma/utils/constants"

// ** Custom Hooks
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"

import { useModal } from "@stores/modal-store"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

interface Props {
  taskId?: number
  responsible: Array<DictType> | Array<number>
  parentTaskId?: number
  onSubmit?: (idArray: number[]) => Promise<void>
}

const AssignToUserTask = ({ onSubmit, taskId, responsible, parentTaskId }: Props) => {
  const modal = useModal()
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext)

  const isHebrew = locale === "he"
  const responsibleArr = responsible || []
  const organizationUsers = authConfig?.organizationUsers || []

  const [searchText, setSearchText] = useState<string>("")
  const [selectedUser, setSelectedUser] = useState<DictType>({})
  const [isAssignUserList, setIsAssignUserList] = useState<Boolean>(false)
  const [availableUsersArr, setAvailableUserArr] = useState(organizationUsers)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set([]))

  useEffect(() => {
    if (responsible) {
      setSelectedRows(
        new Set(responsible.map((item) => (typeof item === "number" ? item : item.id))),
      )
    }
  }, [responsible])

  const assignTaskMutation = useMutation({
    mutationKey: ["assign-task-to-users"],
    mutationFn: (users: number[]) =>
      editTaskService({
        responsible_id: users,
        task_id: taskId,
        inform_users: true,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "User not invited") {
        promptReInvite()
      }
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const promptReInvite = () => {
    modal.onOpen()
    modal.setContext("invite")
    modal.setMetaData(selectedUser)
  }

  const handleCheckboxChange = (id: number) => {
    setSelectedRows((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(id)) newSelected.delete(id)
      else newSelected.add(id)

      return newSelected
    })
  }

  const handleSubmit = () => {
    const idArray = Array.from(selectedRows)
    if (onSubmit) {
      onSubmit(idArray)
    } else {
      assignTaskMutation.mutate(idArray)
    }
  }

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const filtered = organizationUsers.filter((user: DictType) =>
        user.name.toLowerCase().includes(searchText.toLowerCase()),
      )
      setAvailableUserArr(filtered)
    }, 300) // Debounce delay in ms

    return () => clearTimeout(delayDebounce) // Cleanup timeout
  }, [searchText])

  const resolvedResponsible = responsibleArr
    .map((obj) => {
      if (typeof obj === "number") {
        return availableUsersArr.find((objAvailable: any) => objAvailable.id === obj)
      }
      return obj
    })
    .filter(Boolean)

  return (
    <Popover>
      <div className='flex items-center gap-1 pl-2'>
        <div className='flex items-center'>
          {resolvedResponsible.slice(0, 2).map((user: any, index: number) => (
            <Tooltip key={user?.id ?? index}>
              <TooltipTrigger asChild>
                <Avatar className='relative -ml-2 h-6 w-6 border-2 border-white shadow-sm first:ml-0'>
                  <AvatarImage
                    src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                  />
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <h4>{user?.name}</h4>
              </TooltipContent>
            </Tooltip>
          ))}
          {resolvedResponsible.length > 2 && (
            <PopoverTrigger asChild onClick={() => setIsAssignUserList(true)}>
              <button
                type='button'
                className='relative -ml-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-blue-100 text-[10px] font-medium text-blue-700 shadow-sm hover:bg-blue-200'
              >
                +{resolvedResponsible.length - 2}
              </button>
            </PopoverTrigger>
          )}
        </div>
        <PopoverTrigger asChild onClick={() => setIsAssignUserList(false)}>
          <button
            type='button'
            className='flex items-center justify-center rounded-full text-muted-foreground hover:text-primary'
          >
            <PlusCircle size={16} />
          </button>
        </PopoverTrigger>
      </div>

      <PopoverContent className={cn("w-72 p-0", isHebrew ? "rtl" : "ltr")}>
        {isAssignUserList ? (
          <div className='flex flex-col'>
            <div className='border-b px-3 py-2'>
              <h4 className='text-sm font-semibold text-foreground'>
                {translate("Assigned Users")}
              </h4>
              <p className='text-xs text-muted-foreground'>
                {resolvedResponsible.length} {translate("selected")}
              </p>
            </div>
            <div
              className='max-h-[280px] overflow-y-auto overscroll-contain p-2'
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {resolvedResponsible.map((user: any) => (
                <div
                  key={user?.id}
                  className='flex items-center gap-2 rounded-md p-2 hover:bg-muted'
                >
                  <Avatar className='h-8 w-8'>
                    <AvatarImage
                      src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                    />
                  </Avatar>
                  <span className='truncate text-sm text-foreground'>{user?.name}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className='flex flex-col'>
            <div className='border-b p-3'>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={translate("Search User")}
                className={cn(
                  "w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-700 transition-all duration-200",
                  "placeholder:text-slate-400",
                  "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
                  "disabled:cursor-not-allowed disabled:bg-slate-50",
                )}
              />
            </div>
            <div
              className='max-h-[260px] overflow-y-auto overscroll-contain p-2'
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {availableUsersArr.length > 0 ? (
                availableUsersArr.map((user: DictType) => (
                  <Label
                    key={user?.id}
                    htmlFor={`user_${user?.id}`}
                    className='flex cursor-pointer items-center gap-2 rounded-md p-2 hover:bg-muted'
                  >
                    <input
                      type='checkbox'
                      id={`user_${user?.id}`}
                      checked={selectedRows.has(user.id)}
                      onChange={() => {
                        handleCheckboxChange(user.id)
                        setSelectedUser(user)
                      }}
                      className={cn(
                        "h-4 w-4 shrink-0 cursor-pointer appearance-none rounded border border-neutral-300 bg-white checked:border-black checked:bg-black checked:before:block checked:before:h-2.5 checked:before:w-1.5 checked:before:rotate-45 checked:before:border-b-2 checked:before:border-r-2 checked:before:border-white checked:before:content-['']",
                        isHebrew
                          ? "checked:before:-translate-x-[3px]"
                          : "checked:before:translate-x-[4px]",
                      )}
                    />
                    <Avatar className='h-7 w-7'>
                      <AvatarImage
                        src={
                          user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
                        }
                      />
                    </Avatar>
                    <span className='flex-1 truncate text-sm text-foreground'>{user?.name}</span>
                  </Label>
                ))
              ) : (
                <p className='py-6 text-center text-sm text-muted-foreground'>
                  {translate("No Users")}
                </p>
              )}
            </div>
            <div className='flex items-center justify-between gap-2 border-t px-3 py-2'>
              <span className='text-xs text-muted-foreground'>
                {selectedRows.size} {translate("selected")}
              </span>
              <PopoverClose asChild>
                <button
                  onClick={handleSubmit}
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), #444CE7",
                    boxShadow: "inset 0px 0px 0px 1.8px rgba(255, 255, 255, 0.25)",
                  }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold text-white transition-opacity duration-200 hover:opacity-90 sm:gap-2 sm:px-7 sm:py-2.5",
                    "disabled:cursor-not-allowed disabled:opacity-60",
                  )}
                >
                  {translate("Submit")}
                </button>
              </PopoverClose>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default AssignToUserTask
